import React, { useEffect, useRef, useState } from "react";
import { useAsyncFn } from "react-use";
import { Table, Typography, Descriptions } from "antd";
import {
  DoubleLeftOutlined,
  DoubleRightOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import Markdown from "react-markdown";
import Loading from "../Loading/Loading";
import { replaceBrWithNewline } from "../../utils/utils";
import { fetchFileDownload } from "../../api/services";
import { formatDate, formatSize } from "../../utils/utils";
import styles from "./index.module.less";
import { locale } from "../../api/config";
import { FormattedMessage } from "react-intl";

const { Paragraph } = Typography;

const StandardTable = ({ request, params, expandable = true }) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const containerRef = useRef();

  const [{ loading, value: dataSource }, fetchFn] = useAsyncFn(
    async (p, ps) => {
      const data = await request?.({
        ...ps,
        pageSize: p.pageSize,
        pageNumber: p.current,
      });
      setPagination({
        total: data.total || 0,
        current: data.pageNumber || 1,
        pageSize: data?.pageSize || 10,
      });
      return data.result || [];
    },
    [params, request]
  );

  useEffect(() => {
    const fetchData = async () => {
      const result = await fetchFn({ current: 1, pageSize: 10 }, params);
      // Do additional logic with result if needed
    };
    fetchData();
  }, [params, fetchFn]); // Include fetchFn in the dependency array

  const columns = [
    {
      title: "NAME",
      dataIndex: "name",
      width: "25%",
      ellipsis: true,
    },
    {
      title: "TYPE",
      width: "25%",
      dataIndex: "category",
      ellipsis: true,
    },
    {
      title: "DATE",
      width: "25%",
      dataIndex: "release_date",
      ellipsis: true,
      render: (v, record) => formatDate(v),
    },
    {
      title: "FILE",
      width: "25%",
      dataIndex: "url",
      ellipsis: true,
      render: (v, record) => (
        <div
          style={{ color: "#0a52bf", cursor: "pointer" }}
          onClick={async () => {
            if (record.external_url) {
              window.open(record.external_url, "_blank");
              return;
            }
            const res = await fetchFileDownload(record.doc_id, {
              verbose: 100,
            });
            if (res.status === 200) {
              window.open(res.result.url, "_blank");
            }
          }}
        >
          <DownloadOutlined />
          <FormattedMessage id="download" />
        </div>
      ),
    },
  ];

  const expandableRender = record => (
    <div
      className={styles.expandedRowContainer}
      ref={containerRef}
    >
      <Descriptions labelStyle={{ fontWeight: 550 }}>
        <Descriptions.Item
          label={
            <strong>
              <FormattedMessage id="FileName" />
            </strong>
          }
        >
          {((record.category === "Manuals" || record.category === "Brochures") ? record.name : record.file_name) || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <strong>
              <FormattedMessage id="Version" />
            </strong>
          }
        >
          {record.version || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          label={
            <strong>
              <FormattedMessage id="Size" />
            </strong>
          }
        >
          {formatSize(record.size) || "-"}
        </Descriptions.Item>
      </Descriptions>
      <Descriptions
        column={1}
        labelStyle={{ fontWeight: 550 }}
        layout="vertical"
      >
        <Descriptions.Item
          className={styles.ten}
          label={
            <strong>
              <FormattedMessage id="Products" />
            </strong>
          }
          contentStyle={{ display: "block" }}
        >
          <Paragraph ellipsis={{ rows: 2, expandable: true, symbol: "more" }}>
            {record.products?.join(", ") || "-"}
          </Paragraph>
        </Descriptions.Item>
        <Descriptions.Item
          className={styles.ten}
          label={
            <strong>
              <FormattedMessage id="Descriptions" />
            </strong>
          }
        >
          {record.desc[locale] || "-"}
        </Descriptions.Item>
        <Descriptions.Item
          className={styles.ten}
          label={
            <strong>
              <FormattedMessage id="ReleaseNotes" />
            </strong>
          }
        >
          <div className={styles.release_note}>
            <Markdown>
              {replaceBrWithNewline(record.release_note[locale]) || "-"}
            </Markdown>
          </div>
        </Descriptions.Item>
        <Descriptions.Item
          className={styles.ten}
          label={
            <strong>
              <FormattedMessage id="Checksum" />
            </strong>
          }
        >
          {record.checksum || "-"}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );

  return (
    <div className={styles.standardTable}>
      <Table
        rowKey="id"
        columns={columns}
        loading={{ indicator: <Loading />, spinning: loading }}
        dataSource={dataSource}
        pagination={{
          ...pagination,
          prevIcon: <DoubleLeftOutlined />,
          nextIcon: <DoubleRightOutlined />,
          showSizeChanger: false,
        }}
        onChange={p => {
          setPagination(prev => ({
            ...prev,
            current: p.current,
            pageSize: p.pageSize,
          }));
          containerRef?.current?.scrollIntoView();
          fetchFn(
            { ...pagination, current: p.current, pageSize: p.pageSize },
            params
          );
        }}
        size="small"
        showHeader={false}
        expandable={
          expandable
            ? {
                expandedRowKeys,
                showExpandIcon: false,
                expandRowByClick: true,
                expandedRowRender: expandableRender,
                onExpandedRowsChange: er => {
                  setExpandedRowKeys(er.length > 0 ? [er.pop()] : []);
                },
              }
            : undefined
        }
        rowClassName={record =>
          expandedRowKeys.includes(record.id) ? "selectedRow" : ""
        }
      />
    </div>
  );
};

export default StandardTable;
